export const MANIFEST_MAX_BYTES = 524288;
export const PACKAGE_MAX_BYTES = 209715200;

export type ReleaseKind = 'app' | 'ocrl' | 'launcher';
export type ReleaseEnvironment = 'dev' | 'uat' | 'production';

const RELEASE_KINDS: readonly string[] = ['app', 'ocrl', 'launcher'];
const RELEASE_ENVIRONMENTS: readonly string[] = ['dev', 'uat', 'production'];
const KIOSK_VERSION_PATTERN = /^(0|[1-9]\d{0,3})\.(0|[1-9]\d{0,3})\.(0|[1-9]\d{0,3})$/;
const SHA256_HEX = /^[0-9a-f]{64}$/;
const GIT_COMMIT = /^[0-9a-f]{40}$/;
const ISO_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
const KEY_ID = /^[a-z0-9][a-z0-9-]{0,31}$/;
const OCRL_VERSION = /^[A-Za-z0-9._-]{1,32}$/;
const OCRL_FILES = ['ocrl/lrw.dll', 'ocrl/ocrl.dll'];
const LAUNCHER_FILES = ['launcher/kiosk-launcher.exe'];
const OCRL_NAMES = new Set(['ocrl.dll', 'lrw.dll']);
const WINDOWS_DEVICE_NAME = /^(con|prn|aux|nul|conin\$|conout\$|com[0-9¹²³]|lpt[0-9¹²³]) *(\.|$)/i;

const MANIFEST_KEYS = ['format', 'kind', 'version', 'build', 'git_commit', 'created_at', 'key_id', 'environment_policy', 'min_launcher_version', 'journal_schema', 'journal_breaking', 'octopus_affecting', 'ocrl', 'package', 'files'];
const PACKAGE_KEYS = ['name', 'size', 'sha256'];
const FILE_KEYS = ['path', 'size', 'sha256'];
const OCRL_KEYS = ['version'];

export interface ManifestFile {
  path: string;
  size: number;
  sha256: string;
}

export interface ReleaseManifest {
  format: 1;
  kind: ReleaseKind;
  version: string;
  build: number;
  git_commit: string;
  created_at: string;
  key_id: string;
  environment_policy: ReleaseEnvironment[];
  min_launcher_version: string;
  journal_schema: number | null;
  journal_breaking: boolean;
  octopus_affecting: boolean;
  ocrl: { version: string } | null;
  package: { name: string; size: number; sha256: string };
  files: ManifestFile[];
}

export class ManifestRuleError extends Error {
  constructor(readonly reason: string) {
    super(`release manifest invalid: ${reason}`);
    this.name = 'ManifestRuleError';
  }
}

type Check = (value: unknown) => boolean;

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const isInteger = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value);
const matches = (pattern: RegExp): Check => (value) => typeof value === 'string' && pattern.test(value);
const integerIn = (min: number, max: number): Check => (value) => isInteger(value) && value >= min && value <= max;
const oneOf = (choices: readonly string[]): Check => (value) => typeof value === 'string' && choices.includes(value);
const isBoolean: Check = (value) => typeof value === 'boolean';

function isCalendarInstant(value: string): boolean {
  const parsed = Date.parse(value);
  return !Number.isNaN(parsed) && new Date(parsed).toISOString().slice(0, 10) === value.slice(0, 10);
}

const fail = (path: string, message: string): never => {
  throw new ManifestRuleError(`${path === '' ? 'manifest' : path}: ${message}`);
};

function closedObject(value: unknown, path: string, keys: string[]): Record<string, unknown> {
  if (!isRecord(value)) return fail(path, 'Expected object');
  const unknown = Object.keys(value).filter((key) => !keys.includes(key));
  if (unknown.length > 0) fail(path, `Unrecognized key(s) in object: ${unknown.map((key) => `'${key}'`).join(', ')}`);
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) fail(path === '' ? key : `${path}.${key}`, 'Required');
  }
  return value;
}

function field(record: Record<string, unknown>, path: string, key: string, check: Check): void {
  if (!check(record[key])) fail(path === '' ? key : `${path}.${key}`, 'Invalid');
}

function checkSchema(raw: unknown): ReleaseManifest {
  const manifest = closedObject(raw, '', MANIFEST_KEYS);
  field(manifest, '', 'format', (value) => value === 1);
  field(manifest, '', 'kind', oneOf(RELEASE_KINDS));
  field(manifest, '', 'version', matches(KIOSK_VERSION_PATTERN));
  field(manifest, '', 'build', integerIn(1, Number.MAX_SAFE_INTEGER));
  field(manifest, '', 'git_commit', matches(GIT_COMMIT));
  field(manifest, '', 'created_at', (value) => typeof value === 'string' && ISO_UTC.test(value) && isCalendarInstant(value));
  field(manifest, '', 'key_id', matches(KEY_ID));
  field(manifest, '', 'environment_policy', (value) => Array.isArray(value) && value.length >= 1 && value.every(oneOf(RELEASE_ENVIRONMENTS)));
  field(manifest, '', 'min_launcher_version', matches(KIOSK_VERSION_PATTERN));
  field(manifest, '', 'journal_schema', (value) => value === null || integerIn(1, 2147483647)(value));
  field(manifest, '', 'journal_breaking', isBoolean);
  field(manifest, '', 'octopus_affecting', isBoolean);
  if (manifest.ocrl !== null) {
    const ocrl = closedObject(manifest.ocrl, 'ocrl', OCRL_KEYS);
    field(ocrl, 'ocrl', 'version', matches(OCRL_VERSION));
  }
  const packageInfo = closedObject(manifest.package, 'package', PACKAGE_KEYS);
  field(packageInfo, 'package', 'name', (value) => typeof value === 'string');
  field(packageInfo, 'package', 'size', integerIn(1, PACKAGE_MAX_BYTES));
  field(packageInfo, 'package', 'sha256', matches(SHA256_HEX));
  if (!Array.isArray(manifest.files)) fail('files', 'Expected array');
  const files = manifest.files as unknown[];
  if (files.length < 1) fail('files', 'Array must contain at least 1 element(s)');
  files.forEach((entry, index) => {
    const path = `files.${index}`;
    const file = closedObject(entry, path, FILE_KEYS);
    field(file, path, 'path', (value) => typeof value === 'string' && value.length >= 1 && value.length <= 512);
    field(file, path, 'size', integerIn(0, PACKAGE_MAX_BYTES));
    field(file, path, 'sha256', matches(SHA256_HEX));
  });
  return manifest as unknown as ReleaseManifest;
}

export function entryPathProblem(path: string): string | null {
  if (path.length === 0) return 'empty_path';
  if (/[\u0000-\u001f\u007f]/.test(path)) return 'control_character';
  if (path.includes('\\')) return 'backslash';
  if (path.startsWith('/')) return 'absolute_path';
  if (/^[A-Za-z]:/.test(path)) return 'drive_letter';
  if (path.includes(':')) return 'colon';
  if (/[<>"|?*]/.test(path)) return 'reserved_character';
  for (const segment of path.split('/')) {
    if (segment === '') return 'empty_segment';
    if (segment === '.' || segment === '..') return 'dot_segment';
    if (segment.endsWith('.') || segment.endsWith(' ')) return 'trailing_dot_or_space';
    if (WINDOWS_DEVICE_NAME.test(segment)) return 'device_name';
  }
  return null;
}

export function entrySetProblem(kind: ReleaseKind, paths: string[]): string | null {
  for (const path of paths) {
    const problem = entryPathProblem(path);
    if (problem !== null) return `${problem}: ${path}`;
  }
  const folded = new Set(paths.map((path) => path.toLowerCase()));
  if (folded.size !== paths.length) return 'duplicate_path';
  const sorted = [...paths].sort();
  if (kind === 'ocrl') {
    return sorted.length === OCRL_FILES.length && sorted.every((path, index) => path === OCRL_FILES[index]) ? null : 'ocrl_entries';
  }
  if (kind === 'launcher') {
    return sorted.length === LAUNCHER_FILES.length && sorted[0] === LAUNCHER_FILES[0] ? null : 'launcher_entries';
  }
  for (const path of paths) {
    if (!path.startsWith('app/')) return `outside_app: ${path}`;
    if (path.split('/').some((segment) => OCRL_NAMES.has(segment.toLowerCase()))) return `ocrl_file_in_app: ${path}`;
  }
  return null;
}

export function rawJsonProblem(json: string): 'duplicate_key' | 'number_not_integer' | null {
  const objects: (Set<string> | null)[] = [];
  let expectingKey = false;
  for (let at = 0; at < json.length; at += 1) {
    const char = json[at];
    if (char === '"') {
      let end = at + 1;
      while (json[end] !== '"') end += json[end] === '\\' ? 2 : 1;
      const keys = objects[objects.length - 1];
      if (expectingKey && keys) {
        const key = JSON.parse(json.slice(at, end + 1)) as string;
        if (keys.has(key)) return 'duplicate_key';
        keys.add(key);
      }
      expectingKey = false;
      at = end;
    } else if (char === '-' || (char >= '0' && char <= '9')) {
      let end = at;
      while (end < json.length && /[-+0-9.eE]/.test(json[end])) end += 1;
      if (/[.eE]/.test(json.slice(at, end))) return 'number_not_integer';
      at = end - 1;
    } else if (char === '{') {
      objects.push(new Set());
      expectingKey = true;
    } else if (char === '[') {
      objects.push(null);
      expectingKey = false;
    } else if (char === '}' || char === ']') {
      objects.pop();
      expectingKey = false;
    } else if (char === ',') {
      expectingKey = objects[objects.length - 1] instanceof Set;
    }
  }
  return null;
}

export function parseReleaseManifest(bytes: Uint8Array): ReleaseManifest {
  if (bytes.length === 0 || bytes.length > MANIFEST_MAX_BYTES) throw new ManifestRuleError('size');
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) throw new ManifestRuleError('byte_order_mark');
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    throw new ManifestRuleError('not_utf8');
  }
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new ManifestRuleError('not_json');
  }
  const rawProblem = rawJsonProblem(text);
  if (rawProblem !== null) throw new ManifestRuleError(rawProblem);
  const manifest = checkSchema(raw);
  if (new Set(manifest.environment_policy).size !== manifest.environment_policy.length) throw new ManifestRuleError('environment_policy_duplicate');
  if (manifest.package.name !== `kiosk-${manifest.kind}-${manifest.version}.zip`) throw new ManifestRuleError('package_name');
  if (manifest.kind === 'app' && manifest.journal_schema === null) throw new ManifestRuleError('journal_schema_required');
  if (manifest.kind !== 'app' && manifest.journal_schema !== null) throw new ManifestRuleError('journal_schema_not_app');
  if (manifest.kind !== 'app' && manifest.journal_breaking) throw new ManifestRuleError('journal_breaking_not_app');
  if ((manifest.kind === 'ocrl') !== (manifest.ocrl !== null)) throw new ManifestRuleError('ocrl_block');
  if (manifest.kind === 'ocrl' && !manifest.octopus_affecting) throw new ManifestRuleError('ocrl_not_octopus_affecting');
  for (let index = 1; index < manifest.files.length; index += 1) {
    if (!(manifest.files[index - 1].path < manifest.files[index].path)) throw new ManifestRuleError('files_not_sorted');
  }
  const problem = entrySetProblem(manifest.kind, manifest.files.map((file) => file.path));
  if (problem !== null) throw new ManifestRuleError(`files: ${problem}`);
  return manifest;
}
